import { Header } from '@/components/layout/Header';
import { Footer } from '@/components/layout/Footer';
import { ComplianceSection } from '@/components/portfolio/ComplianceSection';
import { TrustIndicators } from '@/components/portfolio/TrustIndicators';
import { MobileAppsSection } from '@/components/portfolio/MobileAppsSection';
import { WebAppsSection } from '@/components/portfolio/WebAppsSection';
import { SaaSProductsSection } from '@/components/portfolio/SaaSProductsSection';
import { ProjectsShowcase } from '@/components/portfolio/ProjectsShowcase';
import { AIAgentsSection } from '@/components/portfolio/AIAgentsSection';
import { PlatformBadges } from '@/components/portfolio/PlatformBadges';
import { Button } from '@/components/ui/button';
import { ArrowRight, Briefcase } from 'lucide-react';
import { Link } from 'react-router-dom';
import { Head as Helmet } from 'vite-react-ssg';
import { CountUp } from '@/components/ui/CountUp';
import { motion } from 'framer-motion';
import { SchemaOrg } from '@/components/seo/SchemaOrg';
import { aiAgents, aiAgentsFaqs } from '@/data/aiAgents';

const aiAgentsItemListSchema = {
  '@context': 'https://schema.org',
  '@type': 'ItemList',
  name: 'AI Agent Products & Accelerators by Ortem Technologies',
  description: 'In-house AI agent products and accelerators Ortem Technologies builds and adapts for clients across healthcare, finance, e-commerce, logistics, real estate, and enterprise operations.',
  url: 'https://ortemtech.com/portfolio/',
  numberOfItems: aiAgents.length,
  itemListElement: aiAgents.map((agent, i) => ({
    '@type': 'ListItem',
    position: i + 1,
    item: {
      '@type': 'Service',
      name: agent.name,
      description: agent.description,
      serviceType: `${agent.category} AI Agent`,
      provider: {
        '@type': 'Organization',
        name: 'Ortem Technologies',
        url: 'https://ortemtech.com',
      },
      url: agent.relatedServiceSlug
        ? `https://ortemtech.com/services/${agent.relatedServiceSlug}/`
        : 'https://ortemtech.com/portfolio/',
    },
  })),
};

const aiAgentsFaqSchema = {
  '@context': 'https://schema.org',
  '@type': 'FAQPage',
  mainEntity: aiAgentsFaqs.map((faq) => ({
    '@type': 'Question',
    name: faq.question,
    acceptedAnswer: {
      '@type': 'Answer',
      text: faq.answer,
    },
  })),
};

const Portfolio = () => {
  return (
    <>
      <Helmet>
        <title>Portfolio | Web, Mobile & AI Projects | Ortem Technologies</title>
        <meta
          name="description"
          content="Explore real client projects across web, mobile, AI agents, AI/ML, cloud and DevOps, and eCommerce in the Ortem Technologies portfolio."
        />
        <meta
          name="keywords"
          content="Ortem Technologies portfolio, AI agent development company, AI agent products, IT project case studies, software development portfolio, digital transformation projects, technology services portfolio"
        />
        <link rel="canonical" href="https://ortemtech.com/portfolio/" />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://ortemtech.com/portfolio/" />
        <meta property="og:title" content="Portfolio | Web, Mobile & AI Projects | Ortem Technologies" />
        <meta property="og:description" content="Explore real client projects across web, mobile, AI agents, AI/ML, cloud and DevOps, and eCommerce in the Ortem Technologies portfolio." />
        <meta property="og:image" content="https://ortemtech.com/og-default.png" />
        <meta property="twitter:card" content="summary_large_image" />
        <meta property="twitter:url" content="https://ortemtech.com/portfolio/" />
        <meta property="twitter:title" content="Portfolio | Web, Mobile & AI Projects | Ortem Technologies" />
        <meta property="twitter:description" content="Explore real client projects across web, mobile, AI agents, AI/ML, cloud and DevOps, and eCommerce in the Ortem Technologies portfolio." />
        <meta property="twitter:image" content="https://ortemtech.com/og-default.png" />
      </Helmet>
      <SchemaOrg schema={aiAgentsItemListSchema} />
      <SchemaOrg schema={aiAgentsFaqSchema} />

      <div className="min-h-screen bg-background">
        <Header />
        <main>
          {/* Hero Section */}
          <section className="relative py-24 md:py-32 overflow-hidden bg-gradient-to-br from-background via-primary/5 to-background">
            {/* Background Elements */}
            <div className="absolute inset-0 overflow-hidden">
              <div className="absolute top-20 right-10 w-72 h-72 bg-primary/10 rounded-full blur-3xl" />
              <div className="absolute bottom-20 left-10 w-96 h-96 bg-blue-500/10 rounded-full blur-3xl" />
            </div>

            <div className="container mx-auto px-4 relative z-10">
              <div className="max-w-4xl mx-auto text-center relative">
                {/* Network Nodes Background Animation */}
                <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 z-[-1] opacity-40 pointer-events-none select-none w-full h-full max-w-[1200px]">
                  <svg viewBox="0 0 1000 600" className="w-full h-full text-primary" fill="none">
                    <defs>
                      <filter id="glow-network-strong">
                        <feGaussianBlur stdDeviation="3" result="coloredBlur" />
                        <feMerge>
                          <feMergeNode in="coloredBlur" />
                          <feMergeNode in="SourceGraphic" />
                        </feMerge>
                      </filter>
                    </defs>

                    {/* Denser Connecting Lines */}
                    {[
                      // Center cluster
                      { x1: 500, y1: 300, x2: 400, y2: 200 }, { x1: 500, y1: 300, x2: 600, y2: 200 },
                      { x1: 500, y1: 300, x2: 400, y2: 400 }, { x1: 500, y1: 300, x2: 600, y2: 400 },
                      { x1: 400, y1: 200, x2: 300, y2: 250 }, { x1: 600, y1: 200, x2: 700, y2: 250 },
                      { x1: 400, y1: 400, x2: 300, y2: 350 }, { x1: 600, y1: 400, x2: 700, y2: 350 },
                      // Outer connections
                      { x1: 300, y1: 250, x2: 200, y2: 150 }, { x1: 700, y1: 250, x2: 800, y2: 150 },
                      { x1: 300, y1: 350, x2: 200, y2: 450 }, { x1: 700, y1: 350, x2: 800, y2: 450 },
                      // Cross connections
                      { x1: 400, y1: 200, x2: 600, y2: 200 }, { x1: 400, y1: 400, x2: 600, y2: 400 },
                      { x1: 300, y1: 250, x2: 300, y2: 350 }, { x1: 700, y1: 250, x2: 700, y2: 350 },
                      // Random scattered
                      { x1: 100, y1: 100, x2: 250, y2: 150 }, { x1: 900, y1: 100, x2: 750, y2: 150 },
                      { x1: 100, y1: 500, x2: 250, y2: 450 }, { x1: 900, y1: 500, x2: 750, y2: 450 },
                      { x1: 500, y1: 100, x2: 500, y2: 200 }, { x1: 500, y1: 500, x2: 500, y2: 400 },
                      { x1: 200, y1: 300, x2: 300, y2: 300 }, { x1: 800, y1: 300, x2: 700, y2: 300 }
                    ].map((line, i) => (
                      <motion.line
                        key={`line-${i}`}
                        x1={line.x1}
                        y1={line.y1}
                        x2={line.x2}
                        y2={line.y2}
                        stroke="currentColor"
                        strokeWidth="1.5"
                        strokeDasharray="4 4"
                        initial={{ opacity: 0.2 }}
                        animate={{ opacity: [0.2, 0.6, 0.2] }}
                        transition={{
                          duration: 3 + Math.random() * 2,
                          repeat: Infinity,
                          ease: "easeInOut",
                          delay: i * 0.1
                        }}
                      />
                    ))}

                    {/* More Nodes */}
                    {[
                      // Center
                      { cx: 500, cy: 300, r: 8 },
                      // Inner Ring
                      { cx: 400, cy: 200, r: 6 }, { cx: 600, cy: 200, r: 6 },
                      { cx: 400, cy: 400, r: 6 }, { cx: 600, cy: 400, r: 6 },
                      // Middle Ring
                      { cx: 300, cy: 250, r: 5 }, { cx: 700, cy: 250, r: 5 },
                      { cx: 300, cy: 350, r: 5 }, { cx: 700, cy: 350, r: 5 },
                      { cx: 500, cy: 150, r: 5 }, { cx: 500, cy: 450, r: 5 },
                      // Outer Ring
                      { cx: 200, cy: 150, r: 4 }, { cx: 800, cy: 150, r: 4 },
                      { cx: 200, cy: 450, r: 4 }, { cx: 800, cy: 450, r: 4 },
                      { cx: 100, cy: 300, r: 4 }, { cx: 900, cy: 300, r: 4 },
                      // Scattered
                      { cx: 150, cy: 100, r: 3 }, { cx: 850, cy: 100, r: 3 },
                      { cx: 150, cy: 500, r: 3 }, { cx: 850, cy: 500, r: 3 },
                      { cx: 350, cy: 100, r: 3 }, { cx: 650, cy: 100, r: 3 },
                      { cx: 350, cy: 500, r: 3 }, { cx: 650, cy: 500, r: 3 },
                    ].map((node, i) => (
                      <motion.g key={`node-${i}`}>
                        <motion.circle
                          cx={node.cx}
                          cy={node.cy}
                          r={node.r}
                          fill="currentColor"
                          filter="url(#glow-network-strong)"
                          initial={{ scale: 0.8, opacity: 0.5 }}
                          animate={{
                            scale: [0.8, 1.3, 0.8],
                            opacity: [0.5, 0.9, 0.5],
                            x: [0, Math.random() * 30 - 15, 0],
                            y: [0, Math.random() * 30 - 15, 0]
                          }}
                          transition={{
                            duration: 3 + Math.random() * 4,
                            repeat: Infinity,
                            ease: "easeInOut",
                            delay: i * 0.1
                          }}
                        />
                      </motion.g>
                    ))}
                  </svg>
                </div>

                <div className="flex items-center justify-center gap-2 mb-6">
                  <Briefcase className="w-5 h-5 text-primary" />
                  <span className="text-primary font-semibold tracking-wider uppercase text-sm">
                    Our Portfolio
                  </span>
                </div>
                <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-foreground mb-6 leading-tight">
                  Building Trust Through
                  <span className="text-primary"> Proven Results</span>
                </h1>
                <p className="text-lg md:text-xl text-muted-foreground mb-8 max-w-2xl mx-auto">
                  Explore our track record of 1,000+ successful projects across healthcare, fintech,
                  logistics, and more. Every project is built with security and compliance at its core.
                </p>
                <div className="flex flex-wrap gap-4 justify-center">
                  <Link to="/contact/">
                    <Button variant="hero" size="lg" className="group">
                      Start Your Project
                      <ArrowRight className="ml-2 w-4 h-4 group-hover:translate-x-1 transition-transform" />
                    </Button>
                  </Link>
                  <Link to="/case-studies/">
                    <Button variant="outline" size="lg">
                      View Case Studies
                    </Button>
                  </Link>
                </div>
              </div>
            </div>
          </section>

          {/* Trust Indicators */}
          <TrustIndicators />

          {/* Compliance Section */}
          <ComplianceSection />

          {/* Platform Badges */}
          <PlatformBadges />

          {/* Projects Showcase */}
          <ProjectsShowcase />

          {/* AI Agent Solutions */}
          <AIAgentsSection />

          {/* Mobile Apps */}
          <MobileAppsSection />

          {/* Ortem SaaS Products */}
          <SaaSProductsSection />

          {/* Web Apps */}
          <WebAppsSection />

          {/* SEO Content Section */}
          <section className="py-16 bg-muted/30">
            <div className="container mx-auto px-4 max-w-4xl">
              <h2 className="text-2xl md:text-3xl font-bold text-foreground mb-6">
                Software Development Portfolio — 1,000+ Projects Delivered
              </h2>
              <div className="prose prose-lg max-w-none text-muted-foreground space-y-4">
                <p>
                  Ortem Technologies is a US-based software development company with a portfolio spanning mobile app development, custom web platforms, AI/ML solutions, cloud infrastructure, and enterprise SaaS products. Every project in this portfolio was built by our engineering team and is live in production — serving real users across healthcare, fintech, logistics, e-commerce, and on-demand services industries.
                </p>
                <p>
                  Our mobile app portfolio includes Flutter and React Native applications published on the Apple App Store and Google Play Store, with ratings averaging 4.7+ stars. Clients include companies across the United States, New Zealand, Australia, and the UK. We specialize in marketplace apps, on-demand service platforms, healthcare apps (HIPAA-compliant), and fintech applications (PCI DSS and SOC 2 compliant).
                </p>
                <p>
                  On the web and SaaS side, our portfolio includes custom CRM platforms, fleet management systems, logistics dashboards, real estate portals, and AI-powered automation tools built with React, Next.js, Node.js, Python, and cloud-native architectures on AWS and Azure. Every system is designed for scalability — our platforms have handled growth from hundreds to millions of users without architectural rewrites.
                </p>
                <p>
                  Our technology stack includes Flutter, React Native, Swift (iOS), Kotlin (Android), React, Next.js, Node.js, Python (FastAPI, Django), PostgreSQL, Firebase, AWS (Lambda, ECS, RDS), Azure, and Elixir/Phoenix for high-concurrency systems. We work with startups building their first MVP and enterprises modernizing legacy platforms.
                </p>
                <p>
                  To see detailed project breakdowns including challenges, solutions, and measurable outcomes, visit our <a href="/case-studies/" className="text-primary hover:underline font-medium">case studies page</a>. To discuss your project, <a href="/contact/" className="text-primary hover:underline font-medium">schedule a free consultation</a>.
                </p>
              </div>
            </div>
          </section>

          {/* CTA Section */}
          <section className="py-20 bg-gradient-to-r from-primary to-blue-600">
            <div className="container mx-auto px-4 text-center">
              <h2 className="text-3xl md:text-4xl font-bold text-white mb-6">
                Ready to Build Something Amazing?
              </h2>
              <p className="text-white/80 text-lg mb-8 max-w-2xl mx-auto">
                Join our <CountUp to="300+" duration={2} delay={0} className="inline-block font-semibold" /> satisfied clients and bring your vision to life with a team you can trust.
              </p>
              <Link to="/contact/">
                <Button variant="secondary" size="lg" className="group bg-white text-primary hover:bg-white/90">
                  Get Free Consultation
                  <ArrowRight className="ml-2 w-4 h-4 group-hover:translate-x-1 transition-transform" />
                </Button>
              </Link>
            </div>
          </section>
        </main>
        <Footer />
      </div>
    </>
  );
};

export default Portfolio;
